import React, { useState, useEffect, useRef } from "react";
import Button from "../../Buttons/Button";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { select } from "framer-motion/client";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const sizeClasses = {
  sm: "px-3 py-1.5",
  md: "px-3 py-2",
  lg: "px-4 py-2.5",
  xl: "px-5 py-3",
};

const fontSizeClasses = {
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

const MonthPicker = ({
  selected,
  onChange,
  placeholder = "Select a month",
  size = "md",
  rounded = "rounded",
  maxDate,
  minDate,
  disabled,
}) => {
  // console.log("selected IN MONTHPICKER", selected);
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(dayjs());
  // const [selectedDate, setSelectedDate] = useState(
  //   selected ? dayjs(selected) : null
  // );
  const [selectedDate, setSelectedDate] = useState(dayjs());
  // console.log("selectedDate in MONTHPICKER", selectedDate);
  useEffect(() => {
    if (selected) {
      setSelectedDate(selected);
      // onChange?.(newDate.format("MMM/YYYY"));
    }
  }, [selected]);
  const dropdownRef = useRef(null);

  const months = Array.from({ length: 12 }, (_, i) =>
    dayjs().month(i).format("MMM")
  );

  const handleYearChange = (increment) => {
    setCurrentDate(currentDate.add(increment, "year"));
  };

  const handleMonthSelect = (month) => {
    const newDate = currentDate.month(months.indexOf(month));
    if (
      (!minDate || newDate.isAfter(minDate)) &&
      (!maxDate || newDate.isBefore(maxDate))
    ) {
      setSelectedDate(newDate);
      onChange?.(newDate.format("MMM/YYYY"));
      setIsOpen(false);
    }
  };

  const isMonthDisabled = (month) => {
    const monthDate = currentDate.month(months.indexOf(month));
    return (
      (minDate && monthDate.isBefore(minDate, "month")) ||
      (maxDate && monthDate.isAfter(maxDate, "month"))
    );
  };

  const isYearDisabled = (increment) => {
    const nextYear = currentDate.add(increment, "year");
    return (
      (minDate && nextYear.isBefore(minDate, "year")) ||
      (maxDate && nextYear.isAfter(maxDate, "year"))
    );
  };

  const [dropdownPosition, setDropdownPosition] = useState({
    vertical: "bottom",
    horizontal: "left",
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (dropdownRef.current && isOpen) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = window.innerWidth - rect.left;
      const dropdownHeight = 300; // approximate height of dropdown
      const dropdownWidth = 256; // w-64 = 16rem = 256px

      // Determine vertical position
      const vertical =
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow
          ? "top"
          : "bottom";

      // Determine horizontal position
      const horizontal = spaceRight < dropdownWidth ? "right" : "left";

      setDropdownPosition({ vertical, horizontal });
    }
  }, [isOpen]);

  // const handleMonthChange = (increment) => {
  //   const newDate = selectedDate
  //     ? selectedDate.add(increment, "month")
  //     : currentDate.add(increment, "month");

  //   if (
  //     (!minDate || newDate.isAfter(minDate)) &&
  //     (!maxDate || newDate.isBefore(maxDate))
  //   ) {
  //     setSelectedDate(newDate);
  //     setCurrentDate(newDate);
  //     onChange?.(newDate.format("MMM/YYYY"));
  //   }
  // };

  const handleMonthChange = (increment) => {
    const newDate = selectedDate
      ? selectedDate.add(increment, "month")
      : currentDate.add(increment, "month");

    if (
      (!minDate || newDate.isSameOrAfter(minDate, "month")) &&
      (!maxDate || newDate.isSameOrBefore(maxDate, "month"))
    ) {
      setSelectedDate(newDate);
      setCurrentDate(newDate);
      onChange?.(newDate.format("MMM/YYYY"));
    }
  };
  const isMonthNavigationDisabled = (increment) => {
    const newDate = selectedDate
      ? selectedDate.add(increment, "month")
      : currentDate.add(increment, "month");
    return (
      (minDate && newDate.isBefore(minDate)) ||
      (maxDate && newDate.isAfter(maxDate))
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex gap-1 p-1 rounded-md bg-white border border-gray-300 hover:border-primary focus:border-primary focus:outline-none">
        <Button
          size="xs-sm"
          variant="text"
          color="dark"
          type="icon"
          icon="chevron_left"
          onClick={() => handleMonthChange(-1)}
          disabled={isMonthNavigationDisabled(-1)}
          className="rounded-none"
        />
        <div className="w-16 flex justify-center">
          <Button
            title={selectedDate ? selectedDate.format("MMM/YYYY") : placeholder}
            size="xs-sm"
            variant="text"
            color="dark"
            onClick={() => !disabled && setIsOpen(!isOpen)}
          />
        </div>
        <Button
          size="xs-sm"
          variant="text"
          color="dark"
          type="icon"
          icon="chevron_right"
          onClick={() => handleMonthChange(1)}
          disabled={isMonthNavigationDisabled(1)}
          className="rounded-none"
        />
      </div>

      {isOpen && (
        <div
          className={`absolute 
          ${
            dropdownPosition.vertical === "bottom"
              ? "top-full mt-1"
              : "bottom-full mb-1"
          }
          ${dropdownPosition.horizontal === "left" ? "left-0" : "right-0"}
          w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50`}
        >
          <div className="flex items-center justify-between gap-3 p-2 border-b">
            <Button
              type="icon"
              size="xs"
              variant="text"
              color="dark"
              icon="chevron_left"
              onClick={() => handleYearChange(-1)}
              disabled={isYearDisabled(-1)}
              className="rounded-none"
            />
            <span className="font-medium">{currentDate.format("YYYY")}</span>
            <Button
              type="icon"
              size="xs"
              variant="text"
              color="dark"
              icon="chevron_right"
              onClick={() => handleYearChange(1)}
              disabled={isYearDisabled(1)}
              className="rounded-none"
            />
          </div>
          <div className="grid grid-cols-3 gap-2 p-3">
            {months.map((month) => {
              const isActive =
                selectedDate &&
                selectedDate.format("MMM") === month &&
                selectedDate.format("YYYY") === currentDate.format("YYYY");
              return (
                <Button
                  title={month}
                  key={month}
                  size="sm"
                  variant="text"
                  color={isActive ? "primary" : "dark"}
                  onClick={() =>
                    !isMonthDisabled(month) && handleMonthSelect(month)
                  }
                  disabled={isMonthDisabled(month)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
